import { LocationChangeAction } from "react-router-redux";
import {
    ReportUrl,
    getCountryListRequest,
    downloadGeographicCountrySvg,
    getAdUnitAndFormatInfoRequest,
    getAgeRequest,
    getGenderRequest,
    getDevicesRequest
} from "@common";
import {
    getOverallFunnelPerformanceDataRequest,
    ReportDataRequestParams,
    getDateDataRequest,
    getEnvironmentDataRequest,
    getWeekdayDataRequest,
    getAgeGroupDataRequest,
    getGenderDataRequest,
    getOSDataRequest,
    getRegionDataRequest,
    campaignMetrics,
    makeBinMetric,
    getAdFormatsDataRequest,
    getAdFormatAdTemplateDataRequest,
    adInteractionActionsMetrics,
    getAdInteractionDataRequest
} from "@api/common";
import { all, select, put } from "redux-saga/effects";
import {
    ReportCampaignListItem,
    reportCampaignListSelector,
    findCountryIsoCodeForGeographicReportSelector
} from "../selector";
import { putCountrySvgToStoreAction } from "../actions";
import { AppState } from "../../store";
import { FetchingState } from "rd-redux-http";

function* loadDynamicTabData(campaign: ReportCampaignListItem, params: ReportDataRequestParams) {
    const metrics = campaignMetrics(campaign.unit).map(m => m.metric);

    yield all([
        getOverallFunnelPerformanceDataRequest.saga({
            ...params,
            metrics
        }),
        getDateDataRequest.saga({ ...params, metrics }),
        getEnvironmentDataRequest.saga({ ...params, metrics }),
        getWeekdayDataRequest.saga({ ...params, metrics })
    ]);
}

function* loadAudienceTabData(campaign: ReportCampaignListItem, params: ReportDataRequestParams) {
    const metrics = campaignMetrics(campaign.unit).map(m => m.metric);

    yield all([
        getAgeRequest.saga({}),
        getGenderRequest.saga({}),
        getDevicesRequest.saga({}),
        getAgeGroupDataRequest.saga({ ...params, metrics }),
        getGenderDataRequest.saga({ ...params, metrics }),
        getOSDataRequest.saga({ ...params, metrics })
    ]);
}

function* loadGeographicTabData(campaign: ReportCampaignListItem, params: ReportDataRequestParams) {
    const metrics = campaignMetrics(campaign.unit).map(m => m.metric);
    const binMetrics = campaignMetrics(campaign.unit)
        .filter(m => !m.isRate)
        .map(m => makeBinMetric(m.metric, 6));

    try {
        yield all([
            getOverallFunnelPerformanceDataRequest.saga({ ...params, metrics }),
            getRegionDataRequest.saga({ ...params, metrics: [...metrics, ...binMetrics] }),
            getCountryListRequest.saga({})
        ]);

        const state: AppState = yield select();
        const selectedCountryIsoCode = findCountryIsoCodeForGeographicReportSelector(state);

        if (
            !selectedCountryIsoCode ||
            (state.reports.geographicReport.countrySVGs &&
                Object.keys(state.reports.geographicReport.countrySVGs).some(key => key === selectedCountryIsoCode))
        ) {
            return;
        }

        const url: string =
            FetchingState.isSuccess(state.reports.countries) &&
            state.reports.countries.data.filter(country => country.iso === selectedCountryIsoCode)[0].mapSvgUrl;

        if (url) {
            const countrySvgResponse = yield* downloadGeographicCountrySvg.saga({
                url: url
            });

            yield put(
                putCountrySvgToStoreAction({ countryIsoCode: selectedCountryIsoCode, countrySVG: countrySvgResponse })
            );
        }
    } catch (e) {
        console.log(e);
    }
}

function* loadCreativeTabData(campaign: ReportCampaignListItem, params: ReportDataRequestParams) {
    const metrics = campaignMetrics(campaign.unit).map(m => m.metric);

    yield* getAdUnitAndFormatInfoRequest.saga({});

    const appState: AppState = yield select();

    const adUnits = FetchingState.getDataOrDefault(appState.mappings.adunits, []);

    const interactionMetrics = adInteractionActionsMetrics(adUnits);

    yield all([
        getAdFormatsDataRequest.saga({ ...params, metrics }),
        getAdFormatAdTemplateDataRequest.saga({ ...params, metrics }),
        getAdInteractionDataRequest.saga({ ...params, metrics: interactionMetrics })
    ]);
}

export function* loadReportingData(action: LocationChangeAction) {
    const urlMatch = ReportUrl.match(action.payload, true);

    if (urlMatch.isMatched) {
        const reportLoadParams: ReportDataRequestParams = {
            ...(urlMatch.query || {}),
            ...urlMatch.params
        } as any;
        if (!reportLoadParams.campaignId) {
            return;
        }

        const campaigns: ReportCampaignListItem[] = yield select(reportCampaignListSelector);
        const campaign = campaigns.find(c => c.campaignId === reportLoadParams.campaignId);
        if (!campaign) {
            return;
        }

        reportLoadParams.timezone = campaign.timezone;

        try {
            switch (urlMatch.params.tab) {
                case "dynamic":
                    yield* loadDynamicTabData(campaign, reportLoadParams);
                    break;
                case "audience":
                    yield* loadAudienceTabData(campaign, reportLoadParams);
                    break;
                case "geographic":
                    yield* loadGeographicTabData(campaign, reportLoadParams);
                    break;
                case "creative":
                    yield* loadCreativeTabData(campaign, reportLoadParams);
            }
        } catch (e) {
            console.error(e);
        }
    }
}
